import { CatalogItem, type CatalogItemCategoryList, type TradeType } from '@kalos/kalos-rpc';
import { toast } from '@kalos/ui';
import { useMemo } from 'react';
import { z } from 'zod';

import { useToolFundCatalogItemCategoryQuery } from '../../../hooks/react-query/useToolFundCatalogItemCategoryQuery';
import { type CatalogItemFilter } from '../../../hooks/react-query/useToolFundCatalogQuery';
import { useBatchGetTradeTypes } from '../../../hooks/react-query/useTradeTypeQuery';
import { downloadCSV, InventoryClientService } from '../../../tools/helpers';

/**
 * The Tool Catalog's search and request, shared by desktop's `ToolFundCatalog` and the phone's
 * `ToolCatalogPhone`, so both ask for the same pages. Moved from `ToolFundCatalog` unchanged.
 */
export const catalogSearchSchema = z.object({
  name: z.string().optional().default(''),
  brand: z.string().optional().default(''),
  categoryId: z.number().optional(),
  externalLink: z.string().optional().default(''),
});

export type CatalogSearchValues = z.infer<typeof catalogSearchSchema>;

export const catalogSearchDefaultValues: CatalogSearchValues = {
  name: '',
  brand: '',
  categoryId: undefined,
  externalLink: '',
};

/** Desktop's catalog request: `%term%` matches, the department's trades, one page. */
export const buildCatalogItemFilter = ({
  searchValues,
  page,
  catalogItemId,
  departmentId,
  tradeTypes,
}: {
  searchValues: CatalogSearchValues;
  page: number;
  catalogItemId?: number;
  departmentId?: number;
  tradeTypes: TradeType[] | undefined;
}): CatalogItemFilter => {
  const searchName = searchValues.name?.trim();
  const searchBrand = searchValues.brand?.trim();
  const searchLink = searchValues.externalLink?.trim();
  const categoryId = searchValues.categoryId;

  // Map department ID to trade type IDs
  let tradeTypeIdList: string | undefined;
  if (departmentId && tradeTypes) {
    const departmentTradeTypes = tradeTypes.filter(
      (tradeType) => tradeType.departmentId === departmentId,
    );
    if (departmentTradeTypes.length > 0) {
      tradeTypeIdList = departmentTradeTypes.map((tradeType) => tradeType.id.toString()).join(',');
    }
  }

  return {
    name: searchName ? `%${searchName}%` : undefined,
    brand: searchBrand ? `%${searchBrand}%` : undefined,
    categoryId: categoryId ? categoryId : undefined,
    id: catalogItemId ? Number(catalogItemId) : undefined,
    externalLink: searchLink ? `%${searchLink}%` : undefined,
    tradeTypeIdList: tradeTypeIdList,
    pageNumber: page,
  };
};

/** The categories (for the filter and for naming a tool's category) and the trade types. */
export const useToolFundCatalogOptions = () => {
  const { data: categories } = useToolFundCatalogItemCategoryQuery({ filter: { pageNumber: -1 } });

  // Get trade types to map department IDs to trade type IDs
  const { data: tradeTypes } = useBatchGetTradeTypes({
    select(data) {
      return data.results || [];
    },
  });

  const categoryOptions = useMemo(() => {
    return (
      categories?.results?.map((category) => ({
        label: category.name,
        value: category.id.toString(),
      })) ?? []
    );
  }, [categories]);

  return { categories, tradeTypes, categoryOptions };
};

/** Desktop's request for one page of the search, with the options it's built from. */
export const useToolFundCatalogRequest = ({
  searchValues,
  page,
  catalogItemId,
  departmentId,
}: {
  searchValues: CatalogSearchValues;
  page: number;
  catalogItemId?: number;
  departmentId?: number;
}) => {
  const { categories, tradeTypes, categoryOptions } = useToolFundCatalogOptions();

  const request = useMemo<CatalogItemFilter>(
    () => buildCatalogItemFilter({ searchValues, page, catalogItemId, departmentId, tradeTypes }),
    [searchValues, page, catalogItemId, departmentId, tradeTypes],
  );

  return { request, categories, tradeTypes, categoryOptions };
};

/**
 * Desktop's Export CSV: every tool matching the search (all pages), with a 1/0 column per trade.
 * A failure toasts "Export failed".
 */
export const exportToolCatalogCsv = async ({
  request,
  tradeTypes,
  categories,
}: {
  request: CatalogItemFilter;
  tradeTypes: TradeType[] | undefined;
  categories: CatalogItemCategoryList | undefined;
}) => {
  try {
    const data = await InventoryClientService.BatchGetCatalogItem(
      CatalogItem.create({ ...request, pageNumber: -1 }),
    );
    const items = data.results ?? [];
    const trades = tradeTypes ?? [];
    const categoryNameById = new Map(
      categories?.results?.map((category) => [category.id, category.name]) ?? [],
    );

    const escape = (value: string | number) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const header = [
      'ID',
      'Category',
      'Name',
      'Brand',
      'Description',
      'Price',
      'Link',
      ...trades.map((trade) => trade.name),
    ];
    const rows = items.map((item) => {
      const itemTradeIds = new Set(item.tradeTypeIdList.split(',').filter(Boolean).map(Number));
      return [
        item.id,
        categoryNameById.get(item.categoryId) ?? '',
        item.name,
        item.brand,
        item.description,
        item.cost.toFixed(2),
        item.externalLink,
        ...trades.map((trade) => (itemTradeIds.has(trade.id) ? 1 : 0)),
      ];
    });

    const csv = [header, ...rows].map((row) => row.map(escape).join(',')).join('\n');
    downloadCSV('tool-catalog', csv);
  } catch (err) {
    toast({
      title: 'Export failed',
      description: err instanceof Error ? err.message : 'Failed to export catalog',
      variant: 'destructive',
    });
  }
};
